import { FC, useMemo, useState } from 'react'
import { useApplication } from '../../../context/application'
import { AttorneyData } from '../../../dtos/applicationData'
import { DefaultTitle, AboutAttorneyContainer } from '../defaultComponents'
import AttorneyFormAboutSection, { AboutSectionData } from './AttorneyFormAboutSection'
import AttorneyFormLicensingSection, { LicensingSectionData } from './AttorneyFormLicensingSection'
import AttorneyFormHistorySection, { HistorySectionData } from './AttorneyFormHistorySection'
import StepBullet from '../../basic/StepBullet'

interface AboutAttorneyProps {
  attorney: AttorneyData;
  attorneyIndex: number;
  isFocused: boolean;
  handlePageResize: () => void;
  showErrors?: boolean;
}

const AboutAttorney: FC<AboutAttorneyProps> = ({ attorney, attorneyIndex, isFocused, handlePageResize, showErrors }) => {
  const { application, saveApplication } = useApplication()
  const [aboutSectionData, setAboutSectionData] = useState<AboutSectionData>(() => {
    return {
      barCardNumber: attorney?.barCardNumber ?? '',
      position: attorney?.position ?? '',
      emailAddress: attorney?.emailAddress ?? '',
      yearsInPrivatePractice: attorney?.yearsInPrivatePractice ?? undefined
    }
  })
  const [licensingSectionData, setLicensingSectionData] = useState<LicensingSectionData>(() => attorney?.license ?? {})
  const [historySectionData, setHistorySectionData] = useState<HistorySectionData>(() => attorney?.workHistory ?? {})
  const [isSectionOpen, setIsSectionOpen] = useState({ about: false, license: false, history: false })

  const handleOpenSection = (section: 'about' | 'license' | 'history') => {
    setIsSectionOpen(prev => ({
      about: section === 'about' ? !prev.about : false,
      license: section === 'license' ? !prev.license : false,
      history: section === 'history' ? !prev.history : false,
    }))
  }

  const validateSections = useMemo(() => {
    const aboutSection = (
      !!attorney?.barCardNumber &&
      !!attorney?.emailAddress &&
      !!attorney?.position &&
      !!attorney?.yearsInPrivatePractice
    )
    
    const licensingSection = (
      (attorney?.license?.isLicensedInOtherState !== undefined) &&
      (attorney?.license?.isLicensedInOtherState !== null) &&
      !!attorney?.license?.licenseDate
    )

    const historySection = (
      !!attorney?.workHistory?.hoursPerWeek &&
      !!attorney?.workHistory?.joinedDate
    )
    
    return {
      aboutSection,
      licensingSection,
      historySection
    }
  }, [attorney])

  const handleSaveDetails = async () => {
    const currentAttorney = { 
      ...attorney, 
      ...aboutSectionData, 
      license: { ...licensingSectionData }, 
      workHistory: { ...historySectionData } 
    }

    const attorneys = [...(application?.attorneysSection?.attorneys ?? [])]

    attorneys[attorneyIndex] = currentAttorney

    await saveApplication({
      ...application,
      stoppedAt: '/attorneys/about',
      attorneysSection: {
        ...application.attorneysSection,
        attorneys
      }
    })
  }
  
  return (
    <AboutAttorneyContainer className={`about-attorney-container ${isFocused ? '' : 'not-focused'}`}>
      <DefaultTitle id={`att__${attorneyIndex}`}>
        {`${attorney?.firstName?.trim()} ${attorney?.lastName?.trim()}`}
      </DefaultTitle>
      <AttorneyFormAboutSection
        aboutSectionData={aboutSectionData}
        setAboutSectionData={setAboutSectionData}
        handlePageResize={handlePageResize}
        formInitialValidation={validateSections.aboutSection}
        onSaveDetails={handleSaveDetails}
        isOpen={isSectionOpen.about}
        handleOpen={() => handleOpenSection('about')}
        showErrors={showErrors}
      />
      {validateSections.aboutSection ? (
        <AttorneyFormLicensingSection
          licensingSectionData={licensingSectionData}
          setLicensingSectionData={setLicensingSectionData}
          handlePageResize={handlePageResize}
          formInitialValidation={validateSections.licensingSection}
          onSaveDetails={handleSaveDetails}
          isOpen={isSectionOpen.license}
          handleOpen={() => handleOpenSection('license')}
          showErrors={showErrors}
        />
      ) : null}
      {validateSections.licensingSection ? (
        <AttorneyFormHistorySection
          historySectionData={historySectionData}
          setHistorySectionData={setHistorySectionData}
          handlePageResize={handlePageResize}
          formInitialValidation={validateSections.historySection}
          onSaveDetails={handleSaveDetails}
          isOpen={isSectionOpen.history}
          handleOpen={() => handleOpenSection('history')}
          showErrors={showErrors}
        />
      ) : null}
    </AboutAttorneyContainer>
  )
}

export default AboutAttorney